import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// 1. Import the remove action
import { getGroupDetails, updateGroup, removeGroupMember, updateSelectedChatGroup,getChatList } from "@/store/slices/chatSlice";
import AddMemberFlow from "./AddMember";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

// Enhanced Icons
const Icons = {
  Close: ({ onClick }) => (
    <div
      onClick={onClick}
      className="cursor-pointer text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-full p-2 transition-colors duration-200"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    </div>
  ),
  Pencil: ({ onClick }) => (
    <div
      onClick={onClick}
      className="ml-2 text-[#000000]cursor-pointer hover:text-[#00a884] transition-colors p-1 cursor-pointer"
      title="Edit Group Info"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
      </svg>
    </div>
  ),
  Camera: () => (
    <div className="flex flex-col items-center justify-center text-white">
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-1">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
        <circle cx="12" cy="13" r="4"></circle>
      </svg>
      <span className="text-[10px] font-bold tracking-wider uppercase">Change Photo</span>
    </div>
  ),
  CameraPlaceholder: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
      <circle cx="12" cy="13" r="4"></circle>
    </svg>
  ),
  AddUser: () => <span className="text-white text-2xl font-light">+</span>,
  UserGeneric: () => <span className="text-gray-300 text-3xl">👤</span>,
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  // 2. Add Trash Icon
  Trash: ({ onClick }) => (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer"
      title="Remove member"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18"></path>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
      </svg>
    </div>
  )
};

const GroupInfoModal = ({ chat, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('chat');

  const { user } = useSelector((state) => state.user);
  
  const { selectedChat, group_details, group_details_loading } = useSelector(
    (state) => state.chat
  );

  const groupId = chat?.group_id || chat?.chat_with?.id || chat?.id || null;

  const [editing, setEditing] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // 3. State for deletion confirmation
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (groupId) {
      dispatch(getGroupDetails(groupId));
    }
  }, [groupId, dispatch]);

  useEffect(() => {
    if (group_details) {
      setName(group_details?.name || "");
      setDescription(group_details?.description || "");
      setPreview(group_details?.image || null);
    }
  }, [group_details]);

  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",
  ];
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type) {
      Toaster.error(t('foldersNotSupported'));
      e.target.value = "";
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      Toaster.error(t('imageOnly'));
      e.target.value = "";
      return;
    }
    if (file) {
      setPreview(URL.createObjectURL(file));
      setImageFile(file);
    }
  };

  const handleUpdate = () => {

    const formData = new FormData();
    formData.append("name", name);
    formData.append("group_id", groupId);
    formData.append("description", description);
    if (imageFile) formData.append("image", imageFile);

    dispatch(updateGroup(formData)).unwrap().then(res => {
      const updatedGroup = res;
      console.log(updatedGroup);
      dispatch(
        updateSelectedChatGroup({
          name: updatedGroup.name,
          image: updatedGroup.image,
          description: updatedGroup.description,
        })
      );

        dispatch(
        getChatList({
          page: 1,
          search: "",     
          filter: "all", 
        })
      );
      Toaster.success(t('groupUpdated'));
    });


    
    setEditing(false);
  };

  // 4. Handle Remove Logic
  const handleConfirmRemove = async () => {
    if (!memberToDelete || !groupId) return;

    setIsDeleting(true);

    const formData = new FormData();
    formData.append("group_id", groupId);
    formData.append("member_id", memberToDelete.user.id);

    try {
      const result = await dispatch(removeGroupMember(formData));
      if (removeGroupMember.fulfilled.match(result)) {
        // Refresh the list after successful deletion
        dispatch(getGroupDetails(groupId));
      }
    } catch (error) {
      console.error("Failed to remove member", error);
    } finally {
      setIsDeleting(false);
      setMemberToDelete(null); // Close modal
    }
  };

  if (!chat || group_details_loading) {
    return (
      <div className="flex flex-col h-full bg-white relative mt-10">
        <div className="flex flex-col items-center gap-2">
          <div className="w-8 h-8 border-4 border-[#00a884] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-500 text-sm">{t('loadingGroupInfo')}</span>
        </div>
      </div>
    );
  }

  const members = group_details?.group_members || [];

  return (
    <div className="flex flex-col h-full bg-white relative">

      {/* 5. Confirmation Modal */}
      {memberToDelete && (
        <div className="absolute inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('removeMember')}</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {t('confirmRemove', { memberName: memberToDelete.user.first_name })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setMemberToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                disabled={isDeleting}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleConfirmRemove}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? t('removing') : t('remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member View Overlay */}
      {showAddMember && (
        <div className="absolute inset-0 z-[60] bg-white h-full w-full">
          <AddMemberFlow
            chatId={groupId}
            currentMembers={members}
            onCancel={() => setShowAddMember(false)}
            onSuccess={() => {
              setShowAddMember(false);
              dispatch(getGroupDetails(groupId));
            }}
          />
        </div>
      )}

      {/* Main Header */}
      {editing && (
        <div className="flex items-center px-4 h-16 bg-white shadow-sm z-10 justify-between">
          <div className="flex items-center">
            {/* <Icons.Close onClick={onClose} /> */}
            <h1 className="ml-4 text-lg font-medium text-gray-800">
              {editing ? t('editGroupInfo') : t('groupDetails')}
            </h1>
          </div>
          <button
            onClick={handleUpdate}
            className="bg-[#00a884] hover:bg-[#008f6f] text-white px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
          >
            <span className="hidden sm:inline">{t('save')}</span>
            <Icons.Check />
          </button>

        </div>)}

      <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#FFFFFF]">
        {/* Group Profile Card */}
        <div className=" bb-2 border-[#37393B26] pb-6 pt-8 px-4 flex flex-col items-center shadow-sm mb-3">

          {/* Image Container */}
          <div className="relative group mb-6">
            <div className="w-40 h-40 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center border border-gray-100 shadow-inner">
              {preview ? (
                <img src={preview} className="w-full h-full object-cover" alt="Group Icon" />
              ) : (
                <div className="text-gray-400"><Icons.CameraPlaceholder /></div>
              )}
            </div>

            {editing && (
              <label className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center cursor-pointer opacity-100 transition-opacity">
                <Icons.Camera />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Name Field */}
          <div className="w-full max-w-md flex flex-col items-center">
            {editing ? (
              <div className="w-full relative group">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('groupName')}
                  maxLength={25}
                  className="w-full text-center text-2xl font-medium border-b-2 border-[#00a884] py-2 focus:outline-none bg-transparent placeholder-gray-300 transition-all"
                  autoFocus
                />
                <span className="absolute right-0 bottom-3 text-xs text-gray-400">
                  {25 - name?.length}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-semibold text-gray-800 break-words text-center">{name}</h2>
                  {group_details?.created_by === user?.id && !editing && <Icons.Pencil onClick={() => setEditing(true)} />}
                </div>
            )}
          </div>

          {/* Description Field */}
          <div className="w-full max-w-md mt-4">
            {editing ? (
              <div className="bg-gray-100 rounded-lg p-3 border border-transparent focus-within:border-[#00a884] focus-within:bg-white focus-within:shadow-sm transition-all">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('descriptionOptional')}
                  rows={3}
                  className="w-full bg-transparent text-gray-700 text-sm focus:outline-none resize-none placeholder-gray-400"
                />
              </div>
            ) : (
              description && (
                <p className="text-gray-500 text-center text-sm leading-relaxed max-w-sm mx-auto">
                  {description}
                </p>
              )
            )}

            {!editing && (
              <p className="text-gray-400 text-xs mt-3 text-center">
                {t('groupMembers', { count: members?.length })}
              </p>
            )}
          </div>
        </div>

        {/* Members Section */}
        <div className="pb-4 min-h-[200px]">
          <div className="px-6 py-4">
            <span className="text-gray text-sm font-medium">{t('members', { count: members?.length })}</span>
          </div>

          {/* Add Member Button */}
          {group_details?.created_by === user?.id && <div
            className="px-4 py-2 flex items-center cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowAddMember(true)}
          >
            <div className="w-10 h-10 bg-[#00a884] rounded-full flex items-center justify-center mr-4 shadow-sm">
              <Icons.AddUser />
            </div>
            <span className="text-gray-800 font-medium">{t('addMember')}</span>
          </div>}

          {/* Member List */}
          {members.map((member, index) => (
            <div
              key={member?.user?.id || index}
              className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer group justify-between"
            >
              <div className="flex items-center flex-1 overflow-hidden">
                <div className="w-10 h-10 rounded-full bg-gray-200 mr-4 flex items-center justify-center overflow-hidden border border-gray-100 flex-shrink-0">
                  {member?.user?.profile_image ? (
                    <img
                      src={member.user.profile_image}
                      className="w-full h-full object-cover"
                      alt="User"
                    />
                  ) : (
                    <Icons.UserGeneric />
                  )}
                </div>

                <div className="flex-1 border-b border-gray-100 pb-3 group-last:border-0 overflow-hidden">
                  <div className="flex items-center gap-2">
                    {user.id !== member?.user.id && <h3 className="text-gray-900 font-medium truncate">
                      {member?.user?.first_name || ""} {member?.user?.last_name || ""}
                    </h3>}
                    {user.id === member?.user.id && 
                      <h3 className="text-gray-900 font-medium truncate">
                        {t('you')}
                      </h3>
                    }
                    {member.type && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize font-medium ${member.type === 'admin'
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-600'
                        }`}>
                        {member.type}
                      </span>
                    )}
                  </div>

                  <p className="text-gray-500 text-xs mt-0.5 truncate">
                    {member?.user?.user_type || member?.user?.email || ""}
                  </p>
                </div>
              </div>

              {/* 6. Delete Button (Added here) */}
              {member.type !== 'admin' && group_details?.created_by === user?.id && < div className="ml-2 group-hover:opacity-100 transition-opacity">
                <Icons.Trash onClick={() => setMemberToDelete(member)} />
              </div>}

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupInfoModal;